import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { CREATE_MULTIPLE_REGULAR_SLOT } from '@/modules/API/slot/regularSlotHandler'
import { ResultCreateRegularSlot } from '@/types/Slots'
import { SelectOptionsDeprecated } from '@/types/Select'
import { UseWeekPatternReturn } from './weekPatternSection/useWeekPattern'

interface UseRegularSlotFormParams {
    daycareId: number
    childIds: number[]
    chooseDays: boolean
    weekPattern: UseWeekPatternReturn
    selectedDays: string[]
    start: string
    end: string
    startDate: string
    endDate: string
    chooseDate: boolean
    dateList: string[]
    place: string
    selectDays: SelectOptionsDeprecated
    refetchRegularSlots: () => void
    handleClose: () => void
    showNotificationSuccess: (msg: string) => void
    showNotificationError: (msg: string) => void
    t: (key: string) => string
}

interface UseRegularSlotFormReturn {
    dayTimes: Record<string, { start: string; end: string }>
    daysMode: boolean
    overlapsSlots: ResultCreateRegularSlot | null
    isOverlapsDialogOpen: boolean
    setOverlapsDialogOpen: (open: boolean) => void
    updateDayTime: (day: string, field: 'start' | 'end', value: string) => void
    getDayLabel: (day: string) => string
    handleCreate: () => Promise<void>
}

export function useRegularSlotForm({
    daycareId,
    childIds,
    chooseDays,
    weekPattern,
    selectedDays,
    start,
    end,
    startDate,
    endDate,
    chooseDate,
    dateList,
    place,
    selectDays,
    refetchRegularSlots,
    handleClose,
    showNotificationSuccess,
    showNotificationError,
    t
}: UseRegularSlotFormParams): UseRegularSlotFormReturn {
    const [dayTimes, setDayTimes] = useState<Record<string, { start: string; end: string }>>({})
    const [overlapsSlots, setOverlapsSlots] = useState<ResultCreateRegularSlot | null>(null)
    const [isOverlapsDialogOpen, setOverlapsDialogOpen] = useState<boolean>(false)

    const daysMode: boolean = chooseDays || weekPattern.chooseWeekPattern

    const [createMultipleRegularSlots] = useMutation(CREATE_MULTIPLE_REGULAR_SLOT)

    useEffect(() => {
        setDayTimes((prev) => {
            const updated: Record<string, { start: string; end: string }> = {}
            for (const day of selectedDays) {
                updated[day] = prev[day] || { start, end }
            }
            return updated
        })
    }, [selectedDays])

    function updateDayTime(day: string, field: 'start' | 'end', value: string): void {
        setDayTimes((prev) => ({
            ...prev,
            [day]: { ...prev[day], [field]: value }
        }))
    }

    function getDayLabel(day: string): string {
        return selectDays[0]?.options.find((o) => o.value === day)?.name ?? day
    }

    function getBaseVariables() {
        return {
            daycareId,
            childIds,
            from: !chooseDate ? startDate : undefined,
            to: !chooseDate ? endDate : undefined,
            dateList: chooseDate ? dateList : [],
            placeId: place === '' ? null : Number(place),
            ...weekPattern.getMutationVariables(startDate)
        }
    }

    function groupDaysByTimes(): Array<{ days: number[]; start: string; end: string }> {
        const groups: Map<string, { days: number[]; start: string; end: string }> = new Map()
        for (const day of selectedDays) {
            const times: { start: string; end: string } = dayTimes[day] || { start, end }
            const key: string = `${times.start}|${times.end}`
            if (!groups.has(key)) {
                groups.set(key, { days: [], start: times.start, end: times.end })
            }
            groups.get(key)!.days.push(Number(day))
        }
        return Array.from(groups.values())
    }

    async function handleCreate(): Promise<void> {
        const baseVars: ReturnType<typeof getBaseVariables> = getBaseVariables()
        try {
            if (daysMode && selectedDays.length > 0) {
                const groups: Array<{ days: number[]; start: string; end: string }> = groupDaysByTimes()
                const aggregated: ResultCreateRegularSlot = {
                    regularSlots: [],
                    occasionalSlots: [],
                    requestSlots: [],
                    otherSlots: []
                }
                for (const group of groups) {
                    const result: Awaited<ReturnType<typeof createMultipleRegularSlots>> =
                        await createMultipleRegularSlots({
                            variables: { ...baseVars, days: group.days, start: group.start, end: group.end }
                        })
                    const data: ResultCreateRegularSlot | undefined = result.data?.createMultipleRegularSlots
                    if (data) {
                        aggregated.regularSlots.push(...data.regularSlots)
                        aggregated.occasionalSlots.push(...data.occasionalSlots)
                        aggregated.requestSlots.push(...data.requestSlots)
                        aggregated.otherSlots.push(...data.otherSlots)
                    }
                }
                const hasOverlaps: boolean =
                    aggregated.regularSlots.length > 0 ||
                    aggregated.occasionalSlots.length > 0 ||
                    aggregated.requestSlots.length > 0 ||
                    aggregated.otherSlots.length > 0
                if (hasOverlaps) {
                    setOverlapsSlots(aggregated)
                    setOverlapsDialogOpen(true)
                } else {
                    refetchRegularSlots()
                    showNotificationSuccess(t('regularSlotsCreated'))
                    handleClose()
                }
            } else {
                const result: Awaited<ReturnType<typeof createMultipleRegularSlots>> = await createMultipleRegularSlots(
                    {
                        variables: { ...baseVars, days: [], start, end }
                    }
                )
                const data: ResultCreateRegularSlot | undefined = result.data?.createMultipleRegularSlots
                if (data) {
                    if (
                        data.regularSlots.length === 0 &&
                        data.occasionalSlots.length === 0 &&
                        data.requestSlots.length === 0 &&
                        data.otherSlots.length === 0
                    ) {
                        refetchRegularSlots()
                        showNotificationSuccess(t('regularSlotsCreated'))
                        handleClose()
                    } else {
                        setOverlapsSlots(data)
                        setOverlapsDialogOpen(true)
                    }
                }
            }
        } catch {
            showNotificationError(t('errorCreatingRegularSlots'))
        }
    }

    return {
        dayTimes,
        daysMode,
        overlapsSlots,
        isOverlapsDialogOpen,
        setOverlapsDialogOpen,
        updateDayTime,
        getDayLabel,
        handleCreate
    }
}
